import { TFunction } from 'i18next';
import { SrtSectionConfig } from '../types';

const buildYesNoOptions = (t: TFunction) => [
    { label: t('detail.srtForm.options.yes'), value: 'si' },
    { label: t('detail.srtForm.options.no'), value: 'no' }
];

const buildSelectOptions = (t: TFunction) => [
    { label: t('detail.srtForm.options.select'), value: '' }
];

export const buildConstructionSiteFields = (t: TFunction) => {
    const selectOptions = buildSelectOptions(t);
    const yesNoOptions = buildYesNoOptions(t);

    return [
        {
            name: 'fechaRecepcionAvisoObra',
            label: t('detail.srtForm.fields.fechaRecepcionAvisoObra'),
            type: 'date',
            gridColumns: 2
        },
        {
            name: 'fechaDeclaradaInicio',
            label: t('detail.srtForm.fields.fechaDeclaradaInicio'),
            type: 'date',
            gridColumns: 2
        },
        {
            name: 'programaSeguridadObra',
            label: t('detail.srtForm.fields.programaSeguridadObra'),
            type: 'select',
            options: selectOptions,
            optionSource: 'programasSeguridadObra',
            gridColumns: 4
        },
        {
            name: 'superficieM2',
            label: t('detail.srtForm.fields.superficieM2'),
            type: 'text',
            inputType: 'number',
            gridColumns: 4
        },
        {
            name: 'numeroPlantas',
            label: t('detail.srtForm.fields.numeroPlantas'),
            type: 'text',
            inputType: 'number',
            gridColumns: 4
        },
        {
            name: 'tipoObra',
            label: t('detail.srtForm.fields.tipoObra'),
            type: 'select',
            options: selectOptions,
            optionSource: 'tiposObras',
            gridColumns: 4
        },
        {
            name: 'actividadesEmpleador',
            label: t('detail.srtForm.fields.actividadesEmpleador'),
            type: 'select',
            options: selectOptions,
            optionSource: 'actividadesObras',
            gridColumns: 4
        },
        {
            name: 'etapaAccidente',
            label: t('detail.srtForm.fields.etapaAccidente'),
            type: 'select',
            options: selectOptions,
            optionSource: 'etapasObras',
            gridColumns: 4
        },
        {
            name: 'programaSeguridad',
            label: t('detail.srtForm.fields.programaSeguridad'),
            type: 'radio',
            options: yesNoOptions,
            gridColumns: 4
        },
        {
            name: 'encuadrePrograma',
            label: t('detail.srtForm.fields.encuadrePrograma'),
            type: 'select',
            options: selectOptions,
            optionSource: 'encuadresProgramas',
            gridColumns: 4
        },
        {
            name: 'fechaFinalizacionActividad',
            label: t('detail.srtForm.fields.fechaFinalizacionActividad'),
            type: 'date',
            gridColumns: 4
        },
        {
            name: 'fechaSuspensionObra',
            label: t('detail.srtForm.fields.fechaSuspensionObra'),
            type: 'date',
            gridColumns: 4
        },
        {
            name: 'fechaReinicioObra',
            label: t('detail.srtForm.fields.fechaReinicioObra'),
            type: 'date',
            gridColumns: 4
        }
    ] as const;
};

export const buildSrtFormSections = (t: TFunction): SrtSectionConfig[] => {
    const yesNoOptions = buildYesNoOptions(t);
    const selectOptions = buildSelectOptions(t);

    return [
        {
            id: 1,
            title: t('detail.srtForm.sections.workerData'),
            fields: [
                {
                    name: 'nroSiniestroArt',
                    label: t('detail.srtForm.fields.nroSiniestroArt'),
                    type: 'text',
                    maxLength: 12,
                    gridColumns: 4
                },
                {
                    name: 'nroDenuncia',
                    label: t('detail.srtForm.fields.nroDenuncia'),
                    type: 'text',
                    inputType: 'number',
                    maxLength: 6,
                    validations: [{ name: 'maxLength', param: 6 }],
                    gridColumns: 4
                },
                {
                    name: 'apellidoTrabajador',
                    label: t('detail.srtForm.fields.apellidoTrabajador'),
                    type: 'text',
                    required: true,
                    validations: [{ name: 'required' }],
                    gridColumns: 4
                },
                {
                    name: 'nombresTrabajador',
                    label: t('detail.srtForm.fields.nombresTrabajador'),
                    type: 'text',
                    required: true,
                    validations: [{ name: 'required' }],
                    gridColumns: 4
                },
                {
                    name: 'cuilTrabajador',
                    label: t('detail.srtForm.fields.cuilTrabajador'),
                    type: 'text',
                    inputType: 'number',
                    maxLength: 11,
                    validations: [{ name: 'fixedLength', param: 11 }],
                    gridColumns: 4
                },
                {
                    name: 'fechaAccidente',
                    label: t('detail.srtForm.fields.fechaAccidente'),
                    type: 'date',
                    gridColumns: 2
                },
                {
                    name: 'horaAccidente',
                    label: t('detail.srtForm.fields.horaAccidente'),
                    type: 'time',
                    gridColumns: 2
                },
                {
                    name: 'descripcion',
                    label: t('detail.srtForm.fields.descripcion'),
                    type: 'textarea',
                    gridColumns: 12
                },
                { type: 'divider', title: t('detail.srtForm.dividers.workerPersonalData') },
                {
                    name: 'fechaNacimiento',
                    label: t('detail.srtForm.fields.fechaNacimiento'),
                    type: 'date',
                    gridColumns: 4
                },
                {
                    name: 'sexo',
                    label: t('detail.srtForm.fields.sexo'),
                    type: 'radio',
                    options: [
                        { label: t('detail.srtForm.options.male'), value: 'masculino' },
                        { label: t('detail.srtForm.options.female'), value: 'femenino' }
                    ],
                    gridColumns: 4
                },
                {
                    name: 'nacionalidad',
                    label: t('detail.srtForm.fields.nacionalidad'),
                    type: 'select',
                    options: selectOptions,
                    optionSource: 'nacionalidades',
                    gridColumns: 4
                },
                {
                    name: 'telefonoTrabajador',
                    label: t('detail.srtForm.fields.telefonoTrabajador'),
                    type: 'text',
                    inputType: 'number',
                    maxLength: 15,
                    validations: [{ name: 'maxLength', param: 15 }],
                    gridColumns: 4
                },
                { type: 'divider', title: t('detail.srtForm.dividers.workerAddress') },
                {
                    name: 'domicilioTrabajador',
                    label: t('detail.srtForm.fields.domicilioTrabajador'),
                    type: 'text',
                    gridColumns: 12
                },
                {
                    name: 'ciudadTrabajador',
                    label: t('detail.srtForm.fields.ciudadTrabajador'),
                    type: 'text',
                    required: true,
                    validations: [{ name: 'required' }],
                    gridColumns: 4
                },
                {
                    name: 'provinciaTrabajador',
                    label: t('detail.srtForm.fields.provinciaTrabajador'),
                    type: 'text',
                    required: true,
                    validations: [{ name: 'required' }],
                    gridColumns: 4
                },
                {
                    name: 'codigoPostalTrabajador',
                    label: t('detail.srtForm.fields.codigoPostalTrabajador'),
                    type: 'text',
                    required: true,
                    validations: [{ name: 'required' }],
                    inputType: 'number',
                    maxLength: 8,
                    gridColumns: 4
                },
                { type: 'divider', title: t('detail.srtForm.dividers.workerLaborData') },
                {
                    name: 'codigoCiuo',
                    label: t('detail.srtForm.fields.codigoCiuo'),
                    type: 'select',
                    required: true,
                    validations: [{ name: 'required' }],
                    options: selectOptions,
                    optionSource: 'ciou',
                    gridColumns: 8
                },
                {
                    name: 'antiguedadPuestoUnidad',
                    label: t('detail.srtForm.fields.antiguedadPuestoUnidad'),
                    type: 'select',
                    required: true,
                    validations: [{ name: 'required' }],
                    options: [
                        { label: t('detail.srtForm.options.days'), value: 'true' },
                        { label: t('detail.srtForm.options.months'), value: 'false' }
                    ],
                    gridColumns: 2
                },
                {
                    name: 'antiguedadPuestoCantidad',
                    label: t('detail.srtForm.fields.antiguedadPuestoCantidad'),
                    type: 'text',
                    required: true,
                    validations: [{ name: 'required' }],
                    inputType: 'number',
                    gridColumns: 2
                },
                {
                    name: 'codigoTipoContrato',
                    label: t('detail.srtForm.fields.codigoTipoContrato'),
                    type: 'select',
                    required: true,
                    validations: [{ name: 'required' }],
                    options: selectOptions,
                    optionSource: 'tiposContrato',
                    gridColumns: 8
                }
            ]
        },
        {
            id: 2,
            title: t('detail.srtForm.sections.employerData'),
            fields: [
                {
                    name: 'razonSocialEmpleador',
                    label: t('detail.srtForm.fields.razonSocialEmpleador'),
                    type: 'text',
                    required: true,
                    validations: [{ name: 'required' }],
                    maxLength: 80,
                    commitOnChange: true,
                    gridColumns: 12
                },
                {
                    name: 'cuitDatosEmpleador',
                    label: t('detail.srtForm.fields.cuitDatosEmpleador'),
                    type: 'text',
                    required: true,
                    inputType: 'number',
                    maxLength: 11,
                    validations: [{ name: 'required' }, { name: 'fixedLength', param: 11 }],
                    commitOnChange: true,
                    gridColumns: 4
                },
                {
                    name: 'telefonoEmpleador',
                    label: t('detail.srtForm.fields.telefonoEmpleador'),
                    type: 'text',
                    inputType: 'number',
                    maxLength: 15,
                    validations: [{ name: 'maxLength', param: 15 }],
                    gridColumns: 4
                },
                {
                    name: 'domicilioEmpleador',
                    label: t('detail.srtForm.fields.domicilioEmpleador'),
                    type: 'text',
                    gridColumns: 4
                },
                {
                    name: 'cpaEmpleador',
                    label: t('detail.srtForm.fields.cpaEmpleador'),
                    type: 'text',
                    maxLength: 8,
                    gridColumns: 4
                },
                {
                    name: 'ciudadEmpleador',
                    label: t('detail.srtForm.fields.ciudadEmpleador'),
                    type: 'text',
                    gridColumns: 4
                },
                {
                    name: 'provinciaEmpleador',
                    label: t('detail.srtForm.fields.provinciaEmpleador'),
                    type: 'text',
                    gridColumns: 4
                },
                {
                    name: 'dotacionActual',
                    label: t('detail.srtForm.fields.dotacionActual'),
                    type: 'text',
                    inputType: 'number',
                    gridColumns: 4
                },
                {
                    name: 'grupoFiscalizacion',
                    label: t('detail.srtForm.fields.grupoFiscalizacion'),
                    type: 'select',
                    options: selectOptions,
                    optionSource: 'gruposFiscalizacion',
                    gridColumns: 4
                },
                {
                    name: 'codigoCiiuEmpleador',
                    label: t('detail.srtForm.fields.codigoCiiuEmpleador'),
                    type: 'select',
                    options: selectOptions,
                    optionSource: 'ciiuCodes',
                    gridColumns: 4
                }
            ]
        },
        {
            id: 3,
            title: t('detail.srtForm.sections.artData'),
            fields: [
                {
                    name: 'denominacionArtEa',
                    label: t('detail.srtForm.fields.denominacionArtEa'),
                    type: 'select',
                    options: selectOptions,
                    optionSource: 'organizaciones',
                    gridColumns: 6
                },
                {
                    name: 'codigoArtEa',
                    label: t('detail.srtForm.fields.codigoArtEa'),
                    type: 'text',
                    inputType: 'number',
                    gridColumns: 6
                }
            ]
        },
        {
            id: 4,
            title: t('detail.srtForm.sections.accidentPlace'),
            fields: [
                {
                    name: 'domicilioLugar',
                    label: t('detail.srtForm.fields.domicilioLugar'),
                    type: 'text',
                    gridColumns: 6
                },
                {
                    name: 'numeroLugar',
                    label: t('detail.srtForm.fields.numeroLugar'),
                    type: 'text',
                    gridColumns: 2
                },
                {
                    name: 'telefonoLugar',
                    label: t('detail.srtForm.fields.telefonoLugar'),
                    type: 'text',
                    inputType: 'number',
                    maxLength: 15,
                    validations: [{ name: 'maxLength', param: 15 }],
                    gridColumns: 4
                },
                {
                    name: 'cpaLugar',
                    label: t('detail.srtForm.fields.codigoPostal'),
                    type: 'text',
                    required: true,
                    validations: [{ name: 'required' }],
                    maxLength: 8,
                    gridColumns: 2
                },
                {
                    name: 'lugarCPA',
                    label: t('detail.srtForm.fields.lugarCPA'),
                    type: 'text',
                    maxLength: 8,
                    validations: [{ name: 'fixedLength', param: 8 }],
                    gridColumns: 2
                },
                {
                    name: 'ciudadLugar',
                    label: t('detail.srtForm.fields.ciudadLugar'),
                    type: 'text',
                    required: true,
                    validations: [{ name: 'required' }],
                    gridColumns: 4
                },
                {
                    name: 'provinciaLugar',
                    label: t('detail.srtForm.fields.provinciaLugar'),
                    type: 'text',
                    required: true,
                    validations: [{ name: 'required' }],
                    gridColumns: 4
                },
                { type: 'divider', title: t('detail.srtForm.dividers.workCenter') },
                {
                    name: 'razonSocialCentro',
                    label: t('detail.srtForm.fields.razonSocialCentro'),
                    type: 'text',
                    required: true,
                    maxLength: 80,
                    validations: [{ name: 'required' }, { name: 'maxLength', param: 80 }],
                    gridColumns: 8
                },
                {
                    name: 'cuitCentro',
                    label: t('detail.srtForm.fields.cuitCentro'),
                    type: 'text',
                    inputType: 'number',
                    maxLength: 11,
                    validations: [{ name: 'fixedLength', param: 11 }],
                    gridColumns: 4
                },
                {
                    name: 'actividadEconomicaCentro',
                    label: t('detail.srtForm.fields.actividadEconomicaCentro'),
                    type: 'text',
                    gridColumns: 12
                },
                {
                    name: 'codigoCiiuCentro',
                    label: t('detail.srtForm.fields.codigoCiiuCentro'),
                    type: 'select',
                    options: selectOptions,
                    optionSource: 'ciiuCodes',
                    gridColumns: 4
                },
                {
                    name: 'denominacionArtCentro',
                    label: t('detail.srtForm.fields.denominacionArtCentro'),
                    type: 'text',
                    readOnly: true,
                    gridColumns: 4
                },
                {
                    name: 'codigoArtCentro',
                    label: t('detail.srtForm.fields.codigoArtCentro'),
                    type: 'text',
                    inputType: 'number',
                    readOnly: true,
                    gridColumns: 4
                },
                {
                    name: 'grupoFiscalizacionCentro',
                    label: t('detail.srtForm.fields.grupoFiscalizacionCentro'),
                    type: 'select',
                    required: true,
                    validations: [{ name: 'required' }],
                    options: selectOptions,
                    optionSource: 'gruposFiscalizacion',
                    gridColumns: 4
                },
                {
                    name: 'otrosFiscalizacion',
                    label: t('detail.srtForm.fields.otrosFiscalizacion'),
                    type: 'text',
                    gridColumns: 4
                },
                {
                    name: 'nroEstablecimientoObra',
                    label: t('detail.srtForm.fields.nroEstablecimientoObra'),
                    type: 'text',
                    // inputType: 'text' explicito es necesario para que SrtFieldRenderer
                    // aplique el maxLength como atributo HTML real del input (ver
                    // SrtFieldRenderer.tsx: "maxLength: field.inputType === 'text' ? ...").
                    // Sin esto, type: 'text' solo no alcanza y se puede seguir tipeando de mas.
                    inputType: 'text',
                    maxLength: 4,
                    validations: [{ name: 'maxLength', param: 4 }],
                    gridColumns: 4
                },
                {
                    name: 'tipoEstablecimiento',
                    label: t('detail.srtForm.fields.tipoEstablecimiento'),
                    type: 'radio',
                    options: [
                        {
                            label: t('detail.srtForm.options.establecimiento'),
                            value: 'true'
                        },
                        {
                            label: t('detail.srtForm.options.obra'),
                            value: 'false'
                        }
                    ],
                    gridColumns: 4
                },
                {
                    name: 'dotacionActualCentro',
                    label: t('detail.srtForm.fields.dotacionActualCentro'),
                    type: 'text',
                    inputType: 'number',
                    gridColumns: 4
                },
                { type: 'divider', title: t('detail.srtForm.dividers.placeType') },
                {
                    name: 'tipoLugar',
                    label: t('detail.srtForm.fields.tipoLugar'),
                    type: 'select',
                    required: true,
                    validations: [{ name: 'required' }],
                    options: [
                        {
                            label: t('detail.srtForm.options.placeSameEmployer'),
                            value: 'mismo_empleador'
                        }
                    ],
                    optionSource: 'tiposLugares',
                    gridColumns: 12
                },
                {
                    name: 'otrosDatosLugar',
                    label: t('detail.srtForm.fields.otrosDatosLugar'),
                    type: 'textarea',
                    maxLength: 1000,
                    validations: [{ name: 'maxLength', param: 1000 }],
                    gridColumns: 12
                }
            ]
        },
        {
            id: 5,
            title: t('detail.srtForm.sections.accidentCoding'),
            fields: [
                {
                    name: 'codigoZonaCuerpo',
                    label: t('detail.srtForm.fields.codigoZonaCuerpo'),
                    type: 'select',
                    options: selectOptions,
                    optionSource: 'zonasCuerpo',
                    gridColumns: 6
                },
                {
                    name: 'codigoNaturalezaLesion',
                    label: t('detail.srtForm.fields.codigoNaturalezaLesion'),
                    type: 'select',
                    options: selectOptions,
                    optionSource: 'naturalezaLesiones',
                    gridColumns: 6
                },
                {
                    name: 'codigoFormaAccidente',
                    label: t('detail.srtForm.fields.codigoFormaAccidente'),
                    type: 'select',
                    options: selectOptions,
                    optionSource: 'formasAccidentes',
                    gridColumns: 6
                },
                {
                    name: 'codigoAgenteCausante',
                    label: t('detail.srtForm.fields.codigoAgenteCausante'),
                    type: 'select',
                    options: selectOptions,
                    optionSource: 'agentesCausantes',
                    gridColumns: 6
                }
            ]
        },
        {
            id: 6,
            title: t('detail.srtForm.sections.accidentData'),
            fields: [
                {
                    name: 'fechaDatosAccidente',
                    label: t('detail.srtForm.fields.fecha'),
                    type: 'date',
                    gridColumns: 4
                },
                {
                    name: 'horaDatosAccidente',
                    label: t('detail.srtForm.fields.hora'),
                    type: 'time',
                    gridColumns: 4
                },
                {
                    name: 'turnoRotativo',
                    label: t('detail.srtForm.fields.turnoRotativo'),
                    type: 'radio',
                    options: yesNoOptions,
                    gridColumns: 4
                },
                {
                    name: 'horarioHabitualDesde',
                    label: t('detail.srtForm.fields.horarioHabitualDesde'),
                    type: 'time',
                    gridColumns: 4
                },
                {
                    name: 'horarioHabitualHasta',
                    label: t('detail.srtForm.fields.horarioHabitualHasta'),
                    type: 'time',
                    gridColumns: 4
                },
                {
                    name: 'realizabaHorasExtras',
                    label: t('detail.srtForm.fields.realizabaHorasExtras'),
                    type: 'radio',
                    options: yesNoOptions,
                    gridColumns: 4
                },
                {
                    name: 'tareasHabituales',
                    label: t('detail.srtForm.fields.tareasHabituales'),
                    type: 'textarea',
                    gridColumns: 6
                },
                {
                    name: 'tareasMomentoAccidente',
                    label: t('detail.srtForm.fields.tareasMomentoAccidente'),
                    type: 'textarea',
                    gridColumns: 6
                },
                {
                    name: 'descripcionAccidente',
                    label: t('detail.srtForm.fields.descripcionAccidente'),
                    type: 'textarea',
                    required: true,
                    validations: [{ name: 'required' }],
                    gridColumns: 12
                },
                {
                    name: 'hechosNecesarios',
                    label: t('detail.srtForm.fields.hechosNecesarios'),
                    type: 'textarea',
                    required: true,
                    maxLength: 2000,
                    validations: [{ name: 'required' }, { name: 'maxLength', param: 2000 }],
                    showCharacterCounter: true,
                    gridColumns: 12
                }
            ]
        },
        {
            id: 7,
            title: t('detail.srtForm.sections.witnesses'),
            fields: [
                {
                    type: 'repeatable',
                    name: 'testigos',
                    addLabel: t('detail.srtForm.actions.addWitness'),
                    fields: [
                        {
                            name: 'apellidoNombres',
                            label: t('detail.srtForm.fields.apellidoNombres'),
                            type: 'text',
                            gridColumns: 3
                        },
                        {
                            name: 'tipoDocumento',
                            label: t('detail.srtForm.fields.documentType'),
                            type: 'radio',
                            options: [
                                { label: t('detail.srtForm.options.cuil'), value: 'cuil' },
                                { label: t('detail.srtForm.options.dni'), value: 'dni' }
                            ],
                            allowDeselect: false,
                            gridColumns: 2
                        },
                        {
                            name: 'dniCuil',
                            label: t('detail.srtForm.fields.documentNumber'),
                            type: 'text',
                            inputType: 'text',
                            numericOnly: true,
                            maxLength: 11,
                            disabledUntilField: 'tipoDocumento',
                            exactLengthByFieldValue: {
                                fieldName: 'tipoDocumento',
                                lengths: { dni: 8, cuil: 11 }
                            },
                            gridColumns: 2
                        },
                        {
                            name: 'cargo',
                            label: t('detail.srtForm.fields.cargo'),
                            type: 'text',
                            gridColumns: 3
                        },
                        {
                            name: 'fecha',
                            label: t('detail.srtForm.fields.fecha'),
                            type: 'date',
                            gridColumns: 2
                        }
                    ]
                }
            ]
        },
        {
            id: 9,
            title: t('detail.srtForm.sections.otherAffected'),
            fields: [
                {
                    type: 'repeatable',
                    name: 'otrosAccidentados',
                    addLabel: t('detail.srtForm.actions.addAffected'),
                    fields: [
                        {
                            name: 'tipoDocumento',
                            label: t('detail.srtForm.fields.documentType'),
                            type: 'radio',
                            options: [
                                { label: t('detail.srtForm.options.cuil'), value: 'cuil' },
                                { label: t('detail.srtForm.options.dni'), value: 'dni' }
                            ],
                            allowDeselect: false,
                            gridColumns: 2
                        },
                        {
                            name: 'cuil',
                            label: t('detail.srtForm.fields.documentNumber'),
                            type: 'text',
                            inputType: 'text',
                            numericOnly: true,
                            maxLength: 11,
                            commitOnChange: true,
                            disabledUntilField: 'tipoDocumento',
                            exactLengthByFieldValue: {
                                fieldName: 'tipoDocumento',
                                lengths: { dni: 8, cuil: 11 }
                            },
                            gridColumns: 3
                        },
                        {
                            name: 'apellidoNombres',
                            label: t('detail.srtForm.fields.apellidoNombreOtroAccidentado'),
                            type: 'text',
                            gridColumns: 4
                        },
                        {
                            name: 'nroDenuncia',
                            label: t('detail.srtForm.fields.nroDenunciaShort'),
                            type: 'text',
                            inputType: 'number',
                            requiredOnlyIfFieldHasValue: 'cuil',
                            gridColumns: 3
                        },
                        {
                            name: 'patologiaTrazadora',
                            label: t('detail.srtForm.fields.patologiaTrazadora'),
                            type: 'text',
                            maxLength: 100,
                            readOnly: true,
                            readOnlyPlain: true,
                            fullWidthWhenFieldHasValue: 'patologiaTrazadora',
                            gridColumns: 6
                        },
                        {
                            name: 'tipoLesion',
                            label: t('detail.srtForm.fields.tipoLesion'),
                            type: 'text',
                            disabledWhenFieldHasValue: 'patologiaTrazadora',
                            hiddenWhenFieldHasValue: 'patologiaTrazadora',
                            requiredWhenFieldEmpty: 'patologiaTrazadora',
                            requiredOnlyIfFieldHasValue: 'cuil',
                            gridColumns: 6
                        },
                        {
                            name: 'descripcionLesion',
                            label: t('detail.srtForm.fields.descripcionLesion'),
                            type: 'textarea',
                            maxLength: 1000,
                            showCharacterCounter: true,
                            disabledWhenFieldHasValue: 'patologiaTrazadora',
                            hiddenWhenFieldHasValue: 'patologiaTrazadora',
                            requiredWhenFieldEmpty: 'patologiaTrazadora',
                            requiredOnlyIfFieldHasValue: 'cuil',
                            gridColumns: 12
                        }
                    ]
                }
            ]
        },
        {
            id: 10,
            title: t('detail.srtForm.sections.correctiveMeasures'),
            fields: [
                {
                    type: 'repeatable',
                    name: 'medidasCorrectivas',
                    addLabel: t('detail.srtForm.actions.addCause'),
                    fields: [
                        {
                            name: 'nroCausa',
                            label: t('detail.srtForm.fields.nroCausa'),
                            type: 'text',
                            inputType: 'number',
                            readOnly: true,
                            gridColumns: 2
                        },
                        {
                            name: 'hecho',
                            label: t('detail.srtForm.fields.hecho'),
                            type: 'text',
                            required: true,
                            validations: [{ name: 'required' }],
                            gridColumns: 6
                        },
                        {
                            name: 'requiereMedidaCorrectiva',
                            label: t('detail.srtForm.fields.requiresCorrectiveMeasure'),
                            type: 'radio',
                            options: yesNoOptions,
                            defaultValue: 'no',
                            allowDeselect: false,
                            gridColumns: 4
                        }
                    ],

                    nestedRepeatable: {
                        type: 'repeatable',
                        name: 'medidas',
                        addLabel: String(t('detail.srtForm.actions.addCorrectiveMeasure')),
                        visibleWhen: { fieldName: 'requiereMedidaCorrectiva', value: 'si' },
                        fields: [
                            {
                                name: 'nroMedida',
                                label: t('detail.srtForm.fields.nroMedida'),
                                type: 'text',
                                inputType: 'number',
                                gridColumns: 2
                            },
                            {
                                name: 'medidaCorrectiva',
                                label: t('detail.srtForm.fields.medidaCorrectiva'),
                                type: 'text',
                                gridColumns: 4
                            },
                            {
                                name: 'fechaInicio',
                                label: t('detail.srtForm.fields.fechaInicio'),
                                type: 'date',
                                gridColumns: 3
                            },
                            {
                                name: 'fechaFin',
                                label: t('detail.srtForm.fields.fechaFin'),
                                type: 'date',
                                gridColumns: 3
                            }
                        ]
                    }
                }
            ]
        },
        {
            id: 11,
            title: t('detail.srtForm.sections.followUp'),
            fields: [
                {
                    type: 'repeatable',
                    name: 'seguimientos',
                    addLabel: t('detail.srtForm.actions.addFollowUp'),
                    fields: [
                        {
                            name: 'medida',
                            label: t('detail.srtForm.fields.medida'),
                            type: 'select',
                            options: selectOptions,
                            gridColumns: 3
                        },
                        {
                            name: 'fechaPrevista',
                            label: t('detail.srtForm.fields.fechaPrevista'),
                            type: 'date',
                            gridColumns: 3
                        },
                        {
                            name: 'fechaVerificacion',
                            label: t('detail.srtForm.fields.fechaVerificacion'),
                            type: 'date',
                            gridColumns: 3
                        },
                        {
                            name: 'resultado',
                            label: t('detail.srtForm.fields.resultado'),
                            type: 'select',
                            options: [
                                { label: t('detail.srtForm.options.cumple'), value: 'cumple' },
                                { label: t('detail.srtForm.options.incumple'), value: 'incumple' }
                            ],
                            gridColumns: 3
                        }
                    ]
                }
            ]
        },
        {
            id: 12,
            title: t('detail.srtForm.sections.observations'),
            fields: [
                {
                    name: 'observaciones',
                    label: t('detail.srtForm.fields.observaciones'),
                    type: 'textarea',
                    gridColumns: 12
                }
            ]
        },
        {
            id: 13,
            title: t('detail.srtForm.sections.reportResponsible'),
            fields: [
                {
                    name: 'responsableApellido',
                    label: t('detail.srtForm.fields.responsableApellido'),
                    type: 'text',
                    required: true,
                    validations: [{ name: 'required' }],
                    inputType: 'text',
                    gridColumns: 3
                },
                {
                    name: 'responsableNombres',
                    label: t('detail.srtForm.fields.responsableNombres'),
                    type: 'text',
                    required: true,
                    validations: [{ name: 'required' }],
                    inputType: 'text',
                    gridColumns: 3
                },
                {
                    name: 'responsableTitulo',
                    label: t('detail.srtForm.fields.responsableTitulo'),
                    type: 'text',
                    required: true,
                    validations: [{ name: 'required' }],
                    gridColumns: 3
                },
                {
                    name: 'responsableTipoRegistro',
                    label: t('detail.srtForm.fields.responsableTipoRegistro'),
                    type: 'select',
                    required: true,
                    validations: [{ name: 'required' }],
                    options: [
                        { label: t('detail.srtForm.fields.responsableRuth'), value: 'ruth' },
                        { label: t('detail.srtForm.fields.responsableRigu'), value: 'rugu' },
                        {
                            label: t('detail.srtForm.fields.responsableMatricula'),
                            value: 'matricula'
                        }
                    ],
                    gridColumns: 3
                },
                {
                    name: 'responsableNumeroRegistro',
                    label: t('detail.srtForm.fields.responsableNumeroRegistro'),
                    type: 'text',
                    required: true,
                    validations: [{ name: 'required' }],
                    inputType: 'number',
                    gridColumns: 3
                },
                {
                    name: 'responsableColegio',
                    label: t('detail.srtForm.fields.responsableColegio'),
                    type: 'text',
                    required: true,
                    validations: [{ name: 'required' }],
                    gridColumns: 3
                },
                {
                    name: 'responsableFecha',
                    label: t('detail.srtForm.fields.fechaInvestigacion'),
                    type: 'date',
                    required: true,
                    validations: [{ name: 'required' }],
                    gridColumns: 3
                },
                {
                    name: 'responsableFirmador',
                    label: t('detail.srtForm.fields.responsableFirmador'),
                    type: 'radio',
                    required: true,
                    validations: [{ name: 'required' }],
                    options: yesNoOptions,
                    gridColumns: 3
                },
                {
                    type: 'divider',
                    title: t('detail.srtForm.dividers.interviewed')
                },
                {
                    type: 'repeatable',
                    name: 'entrevistados',
                    addLabel: t('detail.srtForm.actions.addInterviewed'),
                    // A diferencia de otros repeatables del formulario (testigos, otros
                    // accidentados, etc.), esta seccion combina campos generales (responsable)
                    // con este repeatable: si se dejan los datos del entrevistado vacios, la
                    // seccion no debe marcarse completa aunque los campos generales si lo esten.
                    required: true,
                    fields: [
                        {
                            name: 'apellidoNombres',
                            label: t('detail.srtForm.fields.apellidoNombreEntrevistado'),
                            type: 'text',
                            required: true,
                            inputType: 'text',
                            maxLength: 34,
                            validations: [{ name: 'required' }, { name: 'maxLength', param: 34 }],
                            showCharacterCounter: true,
                            gridColumns: 3
                        },
                        {
                            name: 'tipoDocumento',
                            label: t('detail.srtForm.fields.documentType'),
                            type: 'radio',
                            required: true,
                            validations: [{ name: 'required' }],
                            options: [
                                { label: t('detail.srtForm.options.cuil'), value: 'cuil' },
                                { label: t('detail.srtForm.options.dni'), value: 'dni' }
                            ],
                            allowDeselect: false,
                            gridColumns: 2
                        },
                        {
                            name: 'dniCuil',
                            label: t('detail.srtForm.fields.documentNumber'),
                            type: 'text',
                            required: true,
                            validations: [{ name: 'required' }],
                            inputType: 'text',
                            numericOnly: true,
                            maxLength: 11,
                            disabledUntilField: 'tipoDocumento',
                            exactLengthByFieldValue: {
                                fieldName: 'tipoDocumento',
                                lengths: { dni: 8, cuil: 11 }
                            },
                            gridColumns: 2
                        },
                        {
                            name: 'cargo',
                            label: t('detail.srtForm.fields.cargo'),
                            type: 'text',
                            required: true,
                            validations: [{ name: 'required' }],
                            gridColumns: 2
                        },
                        {
                            name: 'fecha',
                            label: t('detail.srtForm.fields.fechaEntrevista'),
                            type: 'date',
                            required: true,
                            validations: [{ name: 'required' }],
                            readOnly: true,
                            gridColumns: 2
                        },
                        {
                            name: 'firmo',
                            label: t('detail.srtForm.fields.firmo'),
                            type: 'radio',
                            required: true,
                            validations: [{ name: 'required' }],
                            options: yesNoOptions,
                            gridColumns: 1
                        }
                    ]
                }
            ]
        }
    ];
};
